// noinspection DuplicatedCode

// LeafLogix.Web/Dutchie.Common.Domain/Models/Register/Discount/Bundle Discounts/DiscountModel.cs, :10-50
import type { DiscountPlatformTypeRestriction } from '../wizard/types';

export type Discount = {
  ApplicationMethodId: number;
  CanStackAutomatically: boolean;
  Constraints: Constraint[];
  DiscountCode?: string;
  DiscountDescription: string;
  DiscountMenuDisplayDetails?: DiscountMenuCard | null;
  EndTime?: string;
  ExternalId: string;
  FirstTimeCustomerOnly: number;
  Friday?: boolean;
  Id: number;
  IgnoreNetTax: boolean;
  IsAvailableOnline: boolean;
  IsBundledDiscount: boolean;
  LocationRestrictions: number[];
  LoyaltyPointValue: number | null;
  MaxRedemptions?: number | string;
  MenuDisplayRank?: number | null;
  Monday?: boolean;
  OnlineName?: string;
  PaymentRestrictions?: {
    PayByBankSignupIncentive: boolean;
  };
  PlatformTypeRestrictions?: DiscountPlatformTypeRestriction[] | null;
  RedemptionLimit?: number | string;
  RequireManagerApproval: boolean;
  RestrictToGroupIds: number[];
  RestrictToSegmentIds: number[];
  Reward: Reward;
  Saturday?: boolean;
  SavedWithAdvancedOptions?: boolean;
  StartTime?: string;
  Sunday?: boolean;
  Thursday?: boolean;
  Tuesday?: boolean;
  ValidDateFrom: string;
  ValidDateTo: string;
  Wednesday?: boolean;
};

export type DiscountListItem = {
  ApplicationMethod: string;
  DiscDateFrom: string;
  DiscDateTo: string;
  DiscountCode: string;
  DiscountDescription: string;
  Id: number;
  IsAvailableOnline: boolean;
  MenuDisplayRank: number | null;
  SpringBigDiscountId: string;
};

export type DiscountMenuCardImage = {
  MenuDisplayImageUrl: string;
};

export type DiscountMenuCard = {
  DiscountId: number;
  DiscountMenuDisplayId?: number | null;
  MenuDisplayDescription?: string | null;
  MenuDisplayImageUrl?: string | null;
  MenuDisplayName?: string | null;
};

export type DiscountFormProps = {
  allDiscounts?: DiscountListItem[];
  brands: BrandRestriction[];
  canEdit: boolean;
  categories: CategoryRestriction[];
  constraint?: Constraint;
  constraintIndex?: number;
  customerTypes?: CustomerTypeRestriction[];
  discount: Discount;
  loading: boolean;
  navigate: (page: string) => void;
  products: ProductRestriction[];
  savedWithAdvancedOptions: boolean;
  setDiscount: (discount: Discount) => void;
  setLoading: (loading: boolean) => void;
  setOptions?: (showAdvanced: boolean) => void;
  strains: StrainRestriction[];
  tags: TagRestriction[];
  tiers: TierRestriction[];
  userLoyalties?: UserLoyaltyIntegrations;
  vendors: VendorRestriction[];
  weights: WeightRestriction[];
};

export type UserLoyaltyIntegrations = {
  useAlpineIq: boolean;
  useFyllo: boolean;
  useSpringBig: boolean;
};

export type DiscountRestrictionProps = {
  activeTab: number;
  brands: BrandRestriction[];
  canEdit: boolean;
  categories: CategoryRestriction[];
  constraint?: Constraint;
  constraintIndex?: number;
  discount: Discount;
  isConstraint?: boolean;
  loading: boolean;
  products: ProductRestriction[];
  savedWithAdvancedOptions: boolean;
  setDiscount: (discount: Discount) => void;
  setLoading: (loading: boolean) => void;
  startTabIndex: number;
  strains: StrainRestriction[];
  tags: TagRestriction[];
  tiers: TierRestriction[];
  vendors: VendorRestriction[];
  weights: WeightRestriction[];
};

export type Constraint = {
  DiscountConstraintId?: number | null;
  DiscountId: number;
  DiscountItemGroupTypeId?: number;
  IncludeNonCannabis: boolean;
  Products: any[];
  Restrictions: Restrictions;
  ThresholdMax?: number;
  ThresholdMin?: number;
  ThresholdTypeId?: number;
};

export type Reward = {
  ApplyToOnlyOneItem: boolean;
  CalculationMethodId: number;
  DiscountId?: number;
  DiscountRewardId?: number | null;
  DiscountValue: number;
  HasThreshold?: boolean;
  HighestOrLowest: 'high' | 'low';
  IncludeNonCannabis: boolean;
  ItemGroupTypeId: number;
  ManualDefaultApplyTo: number;
  Restrictions: Restrictions;
  ThresholdMax?: number;
  ThresholdMin?: number;
  ThresholdTypeId?: number;
};

export enum ThresholdType {
  NoThreshold = 0,
  NumberOfItems = 1,
  SpendingAmount = 2,
  ProductWeight = 3,
}

export enum TotalItemConditionType {
  AtLeast = 0,
  Every = 1,
  Exactly = 2,
  Between = 3,
}

export enum DiscountFirstTimeCustomer {
  NoFirstTimeDiscount = 0,
  ByLocation = 1,
  AllLocations = 2,
}

export type Restriction = {
  IsExclusion: boolean;
  RestrictionIds: number[];
};

export type Restrictions = {
  Brand?: Restriction;
  Category?: Restriction;
  CustomerType?: Restriction;
  InventoryTag?: Restriction;
  Product?: Restriction;
  Strain?: Restriction;
  Tag?: Restriction;
  Tier?: Restriction;
  Vendor?: Restriction;
  Weight?: Restriction;
};

export type StrainRestriction = {
  Abbreviation: string;
  ExternalId?: number;
  isSelected: boolean;
  StrainAbbreviation: string;
  StrainDescription: string;
  StrainId: number;
  StrainName: string;
  StrainType: string;
};

export type BrandRestriction = {
  BrandId: number;
  BrandName: string;
  isDeletedBrandRestriction?: boolean;
  isSelected: boolean;
};

export type CategoryRestriction = {
  FlowerEquivalent?: number;
  isSelected: boolean;
  LeafLinkCategoryId?: number;
  LeafLinkSubCategoryId?: number;
  LeaflyCategory: string;
  MasterCategory: string;
  MMURAlternativeForms?: any[];
  MMURFormId?: number;
  ProductCategory: string;
  ProductCategoryId: number;
  ProductFlag: string;
  PurchaseLimitCategoryId?: number;
  RecFlowerEquivalent?: number;
  RegulatoryCategoryId?: number;
  SalesAccountId?: number;
  StateInventoryFlag: string;
  TaxCategories?: any[];
  WeedMapsCategoryId?: number;
  WeedMapsSubCategoryId?: number;
};

export type VendorRestriction = {
  Abbreviation: string;
  Address: string;
  City: string;
  ContactEmail: string;
  ContactName: string;
  ContactPhone: string;
  IsActive: boolean;
  isSelected: boolean;
  Notes: string;
  PostalCode: string;
  State: string;
  VendorCode: string;
  VendorId: number;
  VendorName: string;
  VendorTypeId?: number;
};

export type WeightRestriction = {
  isSelected: boolean;
  weight: number;
};

export type CustomerTypeRestriction = {
  CustomerTypeId: string;
  CustomerTypeName: number;
  isSelected: boolean;
};

export type ProductRestriction = {
  Available: number;
  AvailableUnitAbbreviation: string;
  BrandId?: number;
  BrandName: string;
  Category: string;
  CategoryId?: number;
  ChargeCodeId?: number;
  Cultivator: string;
  InvId: number;
  IsRetired: boolean;
  isSelected: boolean;
  MasterCategory: string;
  ProductDesc: string;
  ProductId: number;
  ProductNo: string;
  Vendor: string;
  VendorId?: number;
  Weight: number;
};

export type TagRestriction = {
  isSelected: boolean;
  TagId: number;
  TagName: string;
};

export type TierRestriction = {
  ChargeCodeDesc: string;
  ChargeCodeGroupName: string;
  ChargeCodeId: number;
  ChargeCodeName: string;
  IsExactWeightBuindle: boolean;
  isSelected: boolean;
  PriceId?: number;
  PriceTierGroupId?: number;
  PricingTierPerProduct: boolean;
};

export enum CalculationMethods {
  AmountOff = 1,
  PercentOff = 2,
  PriceToAmount = 3,
  PriceToCostPlusPercent = 4,
  AmountOffTotal = 5,
  PriceToAmountTotal = 6,
  LoyaltyMultiplier = 15,
}

export enum ManualDefaultApplyTo {
  JustOneItem = 1,
  EachItemInRow = 2,
  EveryItemInCart = 3,
  DistributedAmongEveryItem = 4,
}

export enum ApplicationMethodTypeId {
  Automatically = 1,
  Manually = 2,
  ByCode = 3,
  BySpringBig = 4,
  ByAlpineIQ = 5,
  ByFyllo = 6,
  ExternalLoyalty = 7,
  DutchieLoyalty = 8,
}

export enum ApplicationMethod {
  Automatically = 'Automatic',
  ByAlpineIQ = 'AlpineIQ',
  ByCode = 'Code',
  ByFyllo = 'Fyllo',
  BySpringBig = 'SpringBig',
  Manually = 'Manual',
}

export enum DiscountAppliesTo {
  SameProducts = 1,
  DifferentProducts = 2,
  HighestPricedItem = 3,
  LowestPricedItem = 4,
}

export type ApplicationMethodText = `${ApplicationMethod}`;

export const applicationMethodToText = (appMethod: ApplicationMethod): ApplicationMethodText => `${appMethod}`;

export const applicationMethodToTypeId = (appMethod: ApplicationMethod): ApplicationMethodTypeId => {
  switch (appMethod) {
    case ApplicationMethod.Manually: {
      return ApplicationMethodTypeId.Manually;
    }
    case ApplicationMethod.ByCode: {
      return ApplicationMethodTypeId.ByCode;
    }
    case ApplicationMethod.ByFyllo: {
      return ApplicationMethodTypeId.ByFyllo;
    }
    case ApplicationMethod.BySpringBig: {
      return ApplicationMethodTypeId.BySpringBig;
    }
    case ApplicationMethod.ByAlpineIQ: {
      return ApplicationMethodTypeId.ByAlpineIQ;
    }
    case ApplicationMethod.Automatically:
    default: {
      return ApplicationMethodTypeId.Automatically;
    }
  }
};

export const applicationMethodTypeIdToApplicationMethod = (
  appMethodTypeId: ApplicationMethodTypeId
): ApplicationMethod => {
  switch (appMethodTypeId) {
    case ApplicationMethodTypeId.Manually: {
      return ApplicationMethod.Manually;
    }
    case ApplicationMethodTypeId.ByCode: {
      return ApplicationMethod.ByCode;
    }
    case ApplicationMethodTypeId.ByFyllo: {
      return ApplicationMethod.ByFyllo;
    }
    case ApplicationMethodTypeId.BySpringBig: {
      return ApplicationMethod.BySpringBig;
    }
    case ApplicationMethodTypeId.ByAlpineIQ: {
      return ApplicationMethod.ByAlpineIQ;
    }
    case ApplicationMethodTypeId.Automatically:
    default: {
      return ApplicationMethod.Automatically;
    }
  }
};

export enum ItemGroupType {
  All = 5,
  BySKU = 6,
}

export enum HighestOrLowest {
  high = 'high',
  low = 'low',
}

export enum RewardApplyTo {
  all = 'all',
  high = 'high',
  low = 'low',
}

export const rewardApplyToOptions = [
  { name: 'All items', val: RewardApplyTo.all },
  { name: 'Highest priced', val: RewardApplyTo.high },
  { name: 'Lowest priced', val: RewardApplyTo.low },
];

export type discountDropDownOption = {
  autoOrCodeOnly?: boolean;
  description?: string;
  name: string;
  val: number;
};

export const firstTimeCustomerOptions = [
  { name: 'No', val: DiscountFirstTimeCustomer.NoFirstTimeDiscount },
  { name: 'Per location', val: DiscountFirstTimeCustomer.ByLocation },
  { name: 'All locations', val: DiscountFirstTimeCustomer.AllLocations },
];

export const thresholdTypes = [
  { name: 'No condition', val: ThresholdType.NoThreshold },
  { name: 'Total items', val: ThresholdType.NumberOfItems },
  { name: 'Total spend', val: ThresholdType.SpendingAmount },
  { name: 'Total weight', val: ThresholdType.ProductWeight },
];

export const totalItemsConditionTypes = [
  { name: 'At least', val: TotalItemConditionType.AtLeast },
  { name: 'Every', val: TotalItemConditionType.Every },
  { name: 'Exactly', val: TotalItemConditionType.Exactly },
  { name: 'Between', val: TotalItemConditionType.Between },
];

export const constraintThresholdTypes = [
  { name: 'Total items', val: ThresholdType.NumberOfItems },
  { name: 'Total spend', val: ThresholdType.SpendingAmount },
  { name: 'Total weight', val: ThresholdType.ProductWeight },
];

export const calculationMethodOptions: discountDropDownOption[] = [
  { name: 'Amount off - each item', val: CalculationMethods.AmountOff, description: 'A dollar amount off each item ' },
  {
    name: 'Amount off - total amount',
    val: CalculationMethods.AmountOffTotal,
    description: 'A dollar amount off total order amount',
  },
  { name: 'Percent off', val: CalculationMethods.PercentOff, description: 'A percentage off each item' },
  {
    name: 'Price to amount - each item',
    val: CalculationMethods.PriceToAmount,
    description: 'Set the price of each item to a specified amount',
  },
  {
    name: 'Price to amount - total amount',
    val: CalculationMethods.PriceToAmountTotal,
    description: 'Set the price of total order to a specified amount',
  },
  {
    name: 'Price to cost plus percent',
    val: CalculationMethods.PriceToCostPlusPercent,
    description: 'Set price to its wholesale cost plus a percentage',
  },
];

export const calculationMethodOptionsWithLoyaltyMultipliers: discountDropDownOption[] = [
  { name: 'Amount off - each item', val: CalculationMethods.AmountOff, description: 'A dollar amount off each item ' },
  {
    name: 'Amount off - total amount',
    val: CalculationMethods.AmountOffTotal,
    description: 'A dollar amount off total order amount',
  },
  { name: 'Percent off', val: CalculationMethods.PercentOff, description: 'A percentage off each item' },
  {
    name: 'Price to amount - each item',
    val: CalculationMethods.PriceToAmount,
    description: 'Set the price of each item to a specified amount',
  },
  {
    name: 'Price to amount - total amount',
    val: CalculationMethods.PriceToAmountTotal,
    description: 'Set the price of total order to a specified amount',
  },
  {
    name: 'Price to cost plus percent',
    val: CalculationMethods.PriceToCostPlusPercent,
    description: 'Set price to its wholesale cost plus a percentage',
  },
  {
    name: 'Loyalty multiplier',
    val: CalculationMethods.LoyaltyMultiplier,
    description: 'A multiplier applied to the default loyalty accrual rate',
  },
];

export const manualDiscountCalculationOptions: discountDropDownOption[] = [
  { name: 'Amount off', val: CalculationMethods.AmountOff, description: 'A dollar amount off' },
  { name: 'Percent off', val: CalculationMethods.PercentOff, description: 'A percentage off' },
  { name: 'Price to amount', val: CalculationMethods.PriceToAmount, description: 'Set the price to a specific amount' },
  {
    name: 'Price to cost plus percent',
    val: CalculationMethods.PriceToCostPlusPercent,
    description: 'Set price to its wholesale cost plus a percentage',
  },
];

export const manualApplicationMethods = [
  ApplicationMethodTypeId.Manually,
  ApplicationMethodTypeId.BySpringBig,
  ApplicationMethodTypeId.ByAlpineIQ,
  ApplicationMethodTypeId.ByFyllo,
];

export const manualApplicationMethodsWithoutLoyalty = [ApplicationMethodTypeId.Manually];

export const manualDefaultApplyToMethods = [
  { name: 'Just one item', val: ManualDefaultApplyTo.JustOneItem },
  { name: 'Each item in row', val: ManualDefaultApplyTo.EachItemInRow },
  { name: 'Every item in cart', val: ManualDefaultApplyTo.EveryItemInCart },
  { name: 'Distributed among every item', val: ManualDefaultApplyTo.DistributedAmongEveryItem },
];

export const manualDefaultApplyPercentageToMethods = [
  { name: 'Just one item', val: ManualDefaultApplyTo.JustOneItem },
  { name: 'Each item in row', val: ManualDefaultApplyTo.EachItemInRow },
  { name: 'Apply to subtotal', val: ManualDefaultApplyTo.DistributedAmongEveryItem },
];

export const discountAppliesToOptions = [
  { name: 'The same products specified in requirements', val: DiscountAppliesTo.SameProducts },
  { name: 'A different set of products', val: DiscountAppliesTo.DifferentProducts },
  { name: 'The highest priced item from the set of required products', val: DiscountAppliesTo.HighestPricedItem },
  { name: 'The lowest priced item from the set of required products', val: DiscountAppliesTo.LowestPricedItem },
];

export const externalApplicationMethods = [
  ApplicationMethodTypeId.ByAlpineIQ,
  ApplicationMethodTypeId.BySpringBig,
  ApplicationMethodTypeId.ByFyllo,
];

export const appMethodsWithThresholds = [ApplicationMethodTypeId.Automatically, ApplicationMethodTypeId.ByCode];

export const applicationMethodsWithThresholdsWithLoyalty = [
  ApplicationMethodTypeId.Automatically,
  ApplicationMethodTypeId.ByCode,
  ApplicationMethodTypeId.ByAlpineIQ,
  ApplicationMethodTypeId.BySpringBig,
  ApplicationMethodTypeId.ByFyllo,
];
